import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand, useProductName } from "@/context/BrandContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, Eye, EyeSlash } from "@phosphor-icons/react";
import CaptchaWidget, { resetCaptcha } from "@/components/CaptchaWidget";

function BrandMark({ brand, productName, size = "lg", light = true }) {
  const box = size === "lg" ? "w-11 h-11" : "w-10 h-10";
  const icon = size === "lg" ? "w-6 h-6" : "w-5 h-5";
  const titleCls = size === "lg" ? "text-xl" : "text-lg";
  // On purple panels prefer the light login mark; otherwise use the main logo.
  const src = light
    ? (brand.login_logo_url || brand.logo_url)
    : (brand.logo_url || brand.login_logo_url);
  const slogan = brand.slogan || brand.tagline || "";
  const sloganCls = light
    ? "text-primary-foreground/70"
    : "text-muted-foreground";

  const textBlock = productName ? (
    <div className="leading-tight min-w-0">
      <p className={`font-display font-bold tracking-tight ${titleCls}`}>{productName}</p>
      {slogan ? (
        <p className={`mt-0.5 text-[10px] uppercase tracking-[0.22em] ${sloganCls}`}>{slogan}</p>
      ) : null}
    </div>
  ) : (
    <div className="space-y-1.5" aria-hidden>
      <div className={`h-5 w-24 rounded ${light ? "bg-white/20" : "bg-muted"} animate-pulse`} />
      <div className={`h-2.5 w-28 rounded ${light ? "bg-white/15" : "bg-muted/70"} animate-pulse`} />
    </div>
  );

  if (src) {
    return (
      <div className="flex items-center gap-3 relative">
        <img src={src} alt={productName || "Logo"} className={`${box} object-contain`} />
        {textBlock}
      </div>
    );
  }
  if (!productName) {
    return (
      <div className="flex items-center gap-3 relative" aria-hidden>
        <div className={`${box} rounded-xl ${light ? "bg-white/20" : "bg-muted"} animate-pulse`} />
        {textBlock}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 relative">
      <div className={`${box} rounded-xl ${light ? "bg-white/15" : "bg-primary text-primary-foreground"} grid place-items-center`}>
        <Phone className={icon} weight="fill" />
      </div>
      {textBlock}
    </div>
  );
}

export default function Login() {
  const { login, user, ready } = useAuth();
  const { brand } = useBrand();
  const productName = useProductName();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);

  const onCaptcha = useCallback((token) => {
    setCaptchaToken(token);
  }, []);
  const onCaptchaEnabled = useCallback((enabled) => {
    setCaptchaRequired(!!enabled);
    if (!enabled) setCaptchaToken(null);
  }, []);

  useEffect(() => {
    if (ready && user) navigate("/dashboard", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (captchaRequired && !captchaToken) {
      setError("Please complete the captcha.");
      return;
    }
    setLoading(true);
    try {
      await login(email, password, captchaToken);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err) || err.message);
      resetCaptcha();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 -left-16 w-80 h-80 rounded-full bg-black/10 blur-3xl" />
        <BrandMark brand={brand} productName={productName} />
        <div className="relative">
          <h1 className="text-4xl font-display font-bold leading-tight tracking-tight">
            Multi-tenant voice-bot campaigns, orchestrated.
          </h1>
          <p className="mt-4 text-primary-foreground/80 max-w-md">
            Admin, reseller and customer control plane with dual-wallet reservations,
            versioned pricing and strict tenant isolation.
          </p>
        </div>
        <p className="text-xs text-primary-foreground/60 relative">
          © {new Date().getFullYear()} {productName || "\u00a0"} · Control Plane
        </p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandMark brand={brand} productName={productName} size="sm" light={false} />
          </div>
          <h2 className="text-2xl font-display font-semibold tracking-tight">Welcome back</h2>
          <p className="text-sm text-muted-foreground mt-1.5">
            {productName ? `Sign in to your ${productName} account.` : "Sign in to your account."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} required
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" placeholder="you@company.com" className="h-11" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password"
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} value={password} required
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password" placeholder="••••••••" className="h-11 pr-10" />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {show ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <CaptchaWidget onChange={onCaptcha} onEnabled={onCaptchaEnabled} />
            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">{error}</p>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 rounded-lg font-medium">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            No account yet?{" "}
            <Link to="/signup" className="text-primary font-medium hover:underline">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
